import {
  ArrowRightIcon,
  Flex,
  ChannelIcon,
  VisuallyHidden,
  Dialog,
  Image,
  toast,
} from '@buffer-mono/popcorn'
import { getChannelColor } from '~publish/helpers/channels'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'
import csvIcon from './csvIcon.svg'
import styles from './BulkPostDialog.module.css'
import { useState, useCallback } from 'react'
import { PostItems, type ListItem } from '../PostItems'
import { CsvUploader } from '../CsvUploader'
import { useChannelPostLimits } from '~publish/hooks/useChannelPostLimits'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { useOnce } from '~publish/hooks/useOnce'
import { useHasPaymentDetails } from '~publish/contexts/AccountContext'

const BulkPostDialogContent = (): JSX.Element | null => {
  const [items, setItems] = useState<ListItem[]>([])
  const channel = useSelectedChannel()
  const { postsRemaining, isFreePlan } = useChannelPostLimits()

  // Track dialog view
  const commonTrackingProperties = useCommonTrackingProperties()
  const hasPaymentDetails = useHasPaymentDetails()
  useOnce((): boolean => {
    if (commonTrackingProperties?.organizationId && channel) {
      BufferTrackerReact.dialogViewed({
        clientName: 'publishWeb',
        dialogName: 'bulkPostUpload',
        product: 'publish',
        ...commonTrackingProperties,
      })
      return true
    }
    return false
  }, [commonTrackingProperties, channel])

  // Track dialog close
  const handleDialogClose = useCallback(
    (closeAction: 'success' | 'cancel') => {
      if (commonTrackingProperties?.organizationId && channel) {
        BufferTrackerReact.modalDismissed({
          modalName: 'bulkPostUpload',
          method: closeAction,
          hasPaymentDetails,
          ...commonTrackingProperties,
        })
      }
    },
    [commonTrackingProperties, channel, hasPaymentDetails],
  )

  // Prevent the dialog from closing when the user clicks outside the dialog content
  // or presses the escape key. The dialog is only dismissable with the provided close actions.
  // This also aids in tracking at which step the dialog close events are triggered.
  const preventUnwantedDialogClose = useCallback(
    (
      e:
        | CustomEvent<{ originalEvent: PointerEvent }>
        | CustomEvent<{ originalEvent: FocusEvent }>
        | KeyboardEvent,
    ) => {
      e.preventDefault()
    },
    [],
  )

  if (!channel) {
    return null
  }
  const channelColor = getChannelColor(channel.service)
  const style: React.CSSProperties & Record<string, string> = {
    '--channel-icon-color': channelColor,
  }

  const handleUploadComplete = (items: ListItem[]): void => {
    if (isFreePlan && items.length > postsRemaining) {
      toast.error(
        `Limit reached: You have ${postsRemaining} posts remaining on the free plan. ${items.length} posts found.`,
      )
      return
    }
    if (!isFreePlan && items.length > 100) {
      toast.error(
        `Limit reached: Cannot upload more than 100 posts at a time. ${items.length} posts found.`,
      )
      return
    }
    setItems(items)
  }

  return (
    <Dialog.Content
      className={styles.content}
      portal={false}
      onEscapeKeyDown={preventUnwantedDialogClose}
      onPointerDownOutside={preventUnwantedDialogClose}
      onInteractOutside={preventUnwantedDialogClose}
    >
      <Dialog.Header className={styles.header}>
        <Image src={csvIcon} width={16} height={16} alt="CSV Icon" />
        <ArrowRightIcon color="subtle" />
        <Flex
          align="center"
          justify="center"
          className={styles.channelIcon}
          style={style}
        >
          <ChannelIcon color="inverted" type={channel.service} />
          <VisuallyHidden>
            <Dialog.Description>
              Upload a CSV file to schedule multiple posts in bulk.
            </Dialog.Description>
          </VisuallyHidden>
        </Flex>
      </Dialog.Header>
      {items?.length ? (
        <PostItems
          items={items}
          setItems={setItems}
          channelId={channel.id}
          channelName={channel.name}
          onDialogClose={handleDialogClose}
        />
      ) : (
        <CsvUploader
          channelId={channel.id}
          channelService={channel.service}
          onUploadComplete={handleUploadComplete}
        />
      )}
      <Dialog.CloseButton onClick={() => handleDialogClose('cancel')} />
    </Dialog.Content>
  )
}

/**
 * Bulk Post Upload Dialog
 * Separate the Dialog from the Page component
 * But also contain the Dialog.Content component as whatever renders it,
 * also renders Dialog.Portal which prevents local state from resetting
 */
export const BulkPostDialog = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element | null => {
  return (
    <Dialog>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <BulkPostDialogContent />
      </Dialog.Portal>
    </Dialog>
  )
}
